import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { demoOrganizationRepository } from './demoOrganizationRepository';
import { remoteOrganizationRepository } from './remoteOrganizationRepository';
import { env } from '../../shared/config/env';
import type { CreateAccountInput, CreateAssignmentInput, DisableAccountInput, SetMemberCoachesInput, UpdateMemberInput, UpdateStaffInput } from './organization.types';

const organizationKey = ['v4', 'organization'];
const repository = (env.VITE_AUTH_MODE === 'supabase' ? remoteOrganizationRepository : demoOrganizationRepository) as typeof demoOrganizationRepository;

export function useOrganization() {
  return useQuery({ queryKey: organizationKey, queryFn: () => repository.getState() });
}

export function useOrganizationActions() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  if (!session) throw new Error('组织操作需要登录。');
  const actor = session.profile;
  const refresh = () => queryClient.invalidateQueries({ queryKey: organizationKey });
  const createAccountMutation = useMutation({ mutationFn: (input: CreateAccountInput) => repository.createAccount(actor, input), onSuccess: refresh });
  const updateStaffMutation = useMutation({ mutationFn: (input: UpdateStaffInput) => repository.updateStaff(actor, input), onSuccess: refresh });
  const updateMemberMutation = useMutation({ mutationFn: (input: UpdateMemberInput) => repository.updateMember(actor, input), onSuccess: refresh });
  const setMemberCoachesMutation = useMutation({ mutationFn: (input: SetMemberCoachesInput) => repository.setMemberCoaches(actor, input), onSuccess: refresh });
  const disableAccountMutation = useMutation({ mutationFn: (input: DisableAccountInput) => repository.disableAccount(actor, input), onSuccess: refresh });
  const restoreAccountMutation = useMutation({ mutationFn: ({ accountId, reason }: { accountId: string; reason: string }) => repository.restoreAccount(actor, accountId, reason), onSuccess: refresh });
  const createAssignmentMutation = useMutation({ mutationFn: (input: CreateAssignmentInput) => repository.createAssignment(actor, input), onSuccess: refresh });
  const resetMutation = useMutation({ mutationFn: async () => { if (env.VITE_AUTH_MODE !== 'supabase') await demoOrganizationRepository.reset(); }, onSuccess: refresh });
  return {
    createAccount: createAccountMutation.mutateAsync,
    updateStaff: updateStaffMutation.mutateAsync,
    updateMember: updateMemberMutation.mutateAsync,
    setMemberCoaches: setMemberCoachesMutation.mutateAsync,
    disableAccount: disableAccountMutation.mutateAsync,
    restoreAccount: restoreAccountMutation.mutateAsync,
    createAssignment: createAssignmentMutation.mutateAsync,
    reset: resetMutation.mutateAsync,
    pending: createAccountMutation.isPending || updateStaffMutation.isPending || updateMemberMutation.isPending || setMemberCoachesMutation.isPending || disableAccountMutation.isPending || restoreAccountMutation.isPending || createAssignmentMutation.isPending || resetMutation.isPending,
  };
}
